import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { ChatInputProps } from './types';

export interface ChatInputRef {
  focus: () => void;
}

const ChatInput = forwardRef<ChatInputRef, ChatInputProps>(({
  value,
  onChange,
  onSend,
  onStop,
  onAddMessage,
  statusMessage: _statusMessage,
  disabled = false,
  placeholder = "Type a message...",
  theme,
  attachments = [],
  onRemoveAttachment
}, ref) => {
  const PENDING_PARSE_TTL_MS = 2 * 60 * 60 * 1000;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isComposing, setIsComposing] = useState(false);

  // ── Voice dictation (Web Speech API, live transcription) ──────────────────
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<any>(null);
  const dictationBaselineRef = useRef<string>('');
  const valueRef = useRef<string>(value);
  useEffect(() => { valueRef.current = value; }, [value]);

  const dictationSupported = typeof window !== 'undefined' &&
    !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

  const stopDictation = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
      recognitionRef.current = null;
    }
    // Commit finalized baseline (drops any hanging interim text).
    onChange(dictationBaselineRef.current);
    setIsRecording(false);
  };

  const startDictation = async () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      toast.error('Voice recording not supported in this browser. Try Chrome, Edge, or Safari.');
      return;
    }

    // Request mic permission up front. Chrome's SpeechRecognition doesn't
    // always prompt reliably on the first invocation, and a silent
    // 'not-allowed' error is confusing. Hitting getUserMedia first forces
    // the permission prompt if it hasn't been granted.
    if (navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // We don't need to stream; immediately release it.
        stream.getTracks().forEach((t) => t.stop());
      } catch (err: any) {
        console.error('[dictation] mic permission denied', err);
        if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
          toast.error('Microphone access denied. Allow mic access in your browser settings, then click the mic again.');
        } else if (err?.name === 'NotFoundError') {
          toast.error('No microphone detected on this device.');
        } else {
          toast.error('Could not access microphone: ' + (err?.message || err?.name || 'unknown error'));
        }
        return;
      }
    }

    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
      recognitionRef.current = null;
    }
    dictationBaselineRef.current = valueRef.current || '';

    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-GB';

    recognition.onstart = () => {
      console.log('[dictation] started, lang=', recognition.lang);
    };

    recognition.onresult = (event: any) => {
      let finalChunk = '';
      let interimChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalChunk += transcript;
        else interimChunk += transcript;
      }
      console.log('[dictation] onresult', { finalChunk, interimChunk });
      if (finalChunk) {
        const needsSpace = dictationBaselineRef.current && !/\s$/.test(dictationBaselineRef.current);
        dictationBaselineRef.current = dictationBaselineRef.current + (needsSpace ? ' ' : '') + finalChunk.trim();
      }
      const needsInterimSpace = dictationBaselineRef.current && interimChunk && !/\s$/.test(dictationBaselineRef.current);
      const combined = dictationBaselineRef.current + (interimChunk ? (needsInterimSpace ? ' ' : '') + interimChunk : '');
      onChange(combined);
    };

    recognition.onerror = (event: any) => {
      const err = event?.error;
      console.error('[dictation] recognition error', err, event);
      if (err === 'not-allowed' || err === 'service-not-allowed') {
        toast.error('Microphone access denied. Allow mic access in your browser, then try again.');
      } else if (err === 'audio-capture') {
        toast.error('No microphone detected.');
      } else if (err === 'network') {
        toast.error('Voice recognition needs an internet connection.');
      } else if (err && err !== 'no-speech' && err !== 'aborted') {
        toast.error('Voice recording error: ' + err);
      }
    };

    recognition.onend = () => {
      console.log('[dictation] ended');
      onChange(dictationBaselineRef.current);
      recognitionRef.current = null;
      setIsRecording(false);
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setIsRecording(true);
    } catch (err: any) {
      console.error('[dictation] start failed', err);
      toast.error('Could not start voice recording. Please try again.');
    }
  };

  const toggleDictation = () => {
    if (isRecording) stopDictation();
    else void startDictation();
  };

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* noop */ }
        recognitionRef.current = null;
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
    }
  }));

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 100)}px`;
    }
  }, [value]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    const message = value.trim();
    if (message && !disabled) {
      handleSendWithFileContext(message);
    }
  };

  const [isUploading, setIsUploading] = useState(false);
  const [pendingFileKey, setPendingFileKey] = useState<string | null>(null);

  const getActiveConversationId = () => {
    try {
      return localStorage.getItem('ai_active_conversation') || 'default';
    } catch {
      return 'default';
    }
  };

  const getPendingParseStorageKey = () => `ai_pending_parse_upload_${getActiveConversationId()}`;

  const savePendingParseContext = (fileKey: string) => {
    try {
      localStorage.setItem(getPendingParseStorageKey(), JSON.stringify({
        fileKey,
        expiresAt: Date.now() + PENDING_PARSE_TTL_MS
      }));
    } catch {
      // non-blocking
    }
  };

  const clearPendingParseContext = () => {
    try {
      localStorage.removeItem(getPendingParseStorageKey());
    } catch {
      // non-blocking
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(getPendingParseStorageKey());
      if (!raw) return;
      const parsed = JSON.parse(raw) as { fileKey?: string; expiresAt?: number };
      if (!parsed?.fileKey || !parsed?.expiresAt || parsed.expiresAt < Date.now()) {
        localStorage.removeItem(getPendingParseStorageKey());
        return;
      }
      setPendingFileKey(parsed.fileKey);
    } catch {
      // non-blocking
    }
  // We intentionally run this once on mount; conversation id is read from localStorage.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addMsg = (role: 'user' | 'assistant', content: string) => {
    if (onAddMessage) onAddMessage(role, content);
    // Also persist to server conversation (non-blocking)
    try {
      const token = localStorage.getItem('token');
      const csrf = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
      const lastConv = localStorage.getItem('ai_active_conversation');
      if (token && lastConv) {
        fetch(`/api/ai/conversations/${lastConv}/messages`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
          },
          body: JSON.stringify({ role, content }),
        }).catch(() => {});
      }
    } catch { /* non-blocking */ }
  };

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
    return {
      'Authorization': `Bearer ${token}`,
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    };
  };

  // Phase 2: Parse the document after user approval
  const handleProceedWithParse = async () => {
    if (!pendingFileKey) return;
    setIsUploading(true);
    addMsg('assistant', 'Starting framework extraction... This may take a few minutes for large documents.\n\n**Please do not reload or leave this page** — I will report back here when extraction is complete.');

    let clearPending = false;

    try {
      const conversationId = localStorage.getItem('ai_active_conversation');
      const response = await fetch('/api/ai/upload-parse', {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileKey: pendingFileKey,
          ...(conversationId ? { conversation_id: conversationId } : {})
        }),
      });
      const result = await response.json();
      addMsg('assistant', result.message || 'Processing completed.');

      if (result?.success) {
        clearPending = true;
      } else {
        const lowerMessage = String(result?.message || '').toLowerCase();
        if (lowerMessage.includes('session expired')) {
          clearPending = true;
        }
      }
    } catch {
      addMsg('assistant', 'Failed to parse the document due to a temporary error. You can type **"proceed"** to retry with the same upload.');
    } finally {
      setIsUploading(false);
      if (clearPending) {
        setPendingFileKey(null);
        clearPendingParseContext();
      }
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const file = files[0];
    if (!file) return;
    if (!onAddMessage) {
      onChange(`I've uploaded a file: "${file.name}" (${(file.size / 1024).toFixed(1)} KB)`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    addMsg('user', `I've uploaded "${file.name}"`);

    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const isSpreadsheet = ['xlsx', 'xls', 'csv'].includes(ext);

    // ── Spreadsheet → emissions pipeline (parse → import → calculate → report) ──
    if (isSpreadsheet) {
      addMsg('assistant', `Processing **"${file.name}"** as emissions data...\n\nI'll parse the file, import the rows, auto-match emission factors, run the calculation, and report the results here. This may take a moment for large files.`);

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch('/api/ai/upload-emissions-process', {
          method: 'POST',
          headers: getHeaders(),
          body: formData,
        });
        const result = await response.json();
        addMsg('assistant', result.message || (result.success ? 'Processing completed.' : 'Failed to process the file.'));
      } catch {
        addMsg('assistant', 'Failed to process the emissions file. Please try again or use **Emissions → Excel Import** in the sidebar.');
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
      return;
    }

    // ── Non-spreadsheet → compliance framework classification pipeline ──
    addMsg('assistant', `Analyzing "${file.name}"...\n\n**Please do not reload or leave this page until the analysis is complete.**`);

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Phase 1: Classify only
      const response = await fetch('/api/ai/upload-classify', {
        method: 'POST',
        headers: getHeaders(),
        body: formData,
      });
      const result = await response.json();

      if (!result.success) {
        addMsg('assistant', result.message || 'Failed to analyze the document.');
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      const { classification, fileKey, fileName } = result;
      const docType = classification.documentType || 'unknown';
      const confidence = Math.round((classification.confidence || 0) * 100);
      const fileSizeKB = (file.size / 1024).toFixed(1);

      // Build smart response based on document type
      let response_msg = `I've analyzed **"${fileName}"** (${fileSizeKB} KB):\n\n`;
      response_msg += `**Document Type:** ${docType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}\n`;
      response_msg += `**Confidence:** ${confidence}%\n\n`;

      if (docType === 'compliance_framework' || docType === 'standard') {
        response_msg += `This looks like a **compliance standard/framework**. I can extract the full structure (scopes, sections, requirements, evidence fields) and create a draft framework from it.\n\n`;
        response_msg += `**Would you like me to proceed with extracting the compliance framework?**`;
        setPendingFileKey(fileKey);
        savePendingParseContext(fileKey);
      } else if (docType === 'policy' || docType === 'procedure') {
        response_msg += `This appears to be a **${docType}** document, not a compliance framework standard. I can't extract a framework structure from it.\n\n`;
        response_msg += `If this IS a compliance standard, try uploading the official version from the standards body (BSI, ISO, etc.).`;
        setPendingFileKey(null);
        clearPendingParseContext();
      } else {
        response_msg += `This doesn't appear to be a compliance framework document. Framework parsing works best with official compliance standard documents (ISO, BSI, NIST, SOC, etc.).\n\n`;
        response_msg += `If you believe this is a compliance standard, you can try the full Document Parser at /compliance/definition/parser for more options.`;
        setPendingFileKey(null);
        clearPendingParseContext();
      }

      addMsg('assistant', response_msg);
    } catch {
      addMsg('assistant', 'Failed to analyze the document. Please try again.');
      setPendingFileKey(null);
      clearPendingParseContext();
    } finally {
      setIsUploading(false);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Listen for user approval messages
  const originalOnSend = onSend;
  const handleSendWithFileContext = (message: string) => {
    const lower = message.toLowerCase().trim();
    if (pendingFileKey && (lower.includes('yes') || lower.includes('proceed') || lower.includes('go ahead') || lower.includes('extract') || lower.includes('parse it') || lower.includes('do it'))) {
      addMsg('user', message);
      onChange(''); // Clear input
      handleProceedWithParse();
      return;
    }
    if (pendingFileKey && (lower.includes('no') || lower.includes('cancel') || lower.includes('don\'t') || lower.includes('stop'))) {
      addMsg('user', message);
      addMsg('assistant', 'Okay, I won\'t parse this document. You can upload another one anytime using the attachment button.');
      setPendingFileKey(null);
      clearPendingParseContext();
      onChange(''); // Clear input
      return;
    }
    originalOnSend(message);
    onChange(''); // Clear input after sending
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="px-3 pb-3 pt-1">
      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="mb-2 space-y-1">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] ${theme.isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="truncate flex-1">{attachment.name}</span>
              <span className="text-[10px] text-slate-400 shrink-0">{formatFileSize(attachment.size)}</span>
              <button
                onClick={() => onRemoveAttachment?.(attachment.id)}
                className="p-0.5 rounded hover:bg-red-100 hover:text-red-500 transition-colors shrink-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input */}
      <div className={`flex items-end gap-2 ${theme.isDark ? 'bg-slate-800 border-slate-600' : 'bg-slate-50 border-slate-200'} border rounded-xl px-3 py-2 focus-within:border-blue-300 focus-within:ring-1 focus-within:ring-blue-100 transition-all`}>
        {/* Attach */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isUploading}
          className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-40 shrink-0 pb-0.5"
          aria-label="Attach file"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyPress}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          placeholder={placeholder}
          disabled={disabled}
          className={`flex-1 bg-transparent text-[13px] ${theme.isDark ? 'text-white placeholder:text-slate-500' : 'text-slate-800 placeholder:text-slate-400'} outline-none resize-none overflow-hidden min-h-[20px] max-h-[100px] leading-relaxed`}
          rows={1}
        />

        {/* Mic (voice-to-text) */}
        {dictationSupported && (
          <button
            type="button"
            onClick={toggleDictation}
            disabled={disabled}
            aria-label={isRecording ? 'Stop voice recording' : 'Start voice recording'}
            title={isRecording ? 'Stop recording' : 'Record voice to text'}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all disabled:opacity-30 shrink-0 ${
              isRecording
                ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                : theme.isDark
                  ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                  : 'text-slate-400 hover:text-slate-700 hover:bg-slate-200'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 10v2a7 7 0 01-14 0v-2M12 19v4m-4 0h8m-4-4a4 4 0 004-4V7a4 4 0 10-8 0v8a4 4 0 004 4z" />
            </svg>
          </button>
        )}

        {/* Send / Stop */}
        {disabled && onStop ? (
          <button
            onClick={onStop}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-500 text-white hover:bg-red-600 transition-all shrink-0"
            aria-label="Stop"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          </button>
        ) : (
          <button
            onClick={handleSend}
            disabled={!value.trim() || disabled}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-all disabled:opacity-30 disabled:hover:bg-blue-600 shrink-0"
            aria-label="Send"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 19V5m0 0l-5 5m5-5l5 5" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
});

ChatInput.displayName = 'ChatInput';

export default ChatInput;
